/**
 * A workbook, reduced to something a table can render.
 *
 * `CustomFileReader` had no spreadsheet branch: an .xlsx fell through to
 * "Preview is not available for this file type" and a download link, so a
 * user who attached a schedule to a task had to leave the app and open Excel
 * to see three numbers. This is the parse half - pure, so the caps and the
 * empty cases can be pinned without a DOM.
 *
 * THE CAPS ARE THE POINT. A spreadsheet is the one attachment that is
 * routinely enormous, and `sheet_to_json` will happily hand back a hundred
 * thousand rows that React will happily turn into a hundred thousand `<tr>`s.
 * A preview is for looking at, not for auditing, so it stops at a readable
 * size and says how much it is not showing.
 */

export const MAX_PREVIEW_ROWS = 200;
export const MAX_PREVIEW_COLUMNS = 40;

export interface SheetPreview {
  name: string;
  /** Row-major cells, already stringified and already capped. */
  rows: string[][];
  /** Rows in the sheet before the cap. */
  totalRows: number;
  /** Columns in the widest row before the cap. */
  totalColumns: number;
  truncated: boolean;
}

export interface WorkbookPreview {
  sheets: SheetPreview[];
}

/** A cell, as text. Dates and numbers arrive already formatted by `raw:false`. */
const asText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
};

/**
 * One sheet's grid → a capped preview.
 *
 * `grid` is what `XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false })`
 * returns: an array of rows, each a sparse array of cell values. Sparse is
 * load-bearing - a row that starts at column D has three holes in it, and
 * every row has to be padded to the same width or the table's columns stop
 * lining up with its header.
 */
export function toSheetPreview(name: string, grid: unknown[][]): SheetPreview {
  const rows = Array.isArray(grid) ? grid : [];

  // Trailing blank rows are an artefact of how far someone once scrolled, not
  // content. They would otherwise eat the whole row cap.
  let lastUsed = -1;
  rows.forEach((row, index) => {
    const hasContent = Array.isArray(row) && row.some((cell) => asText(cell).trim() !== '');
    if (hasContent) lastUsed = index;
  });
  const used = rows.slice(0, lastUsed + 1);

  const totalRows = used.length;
  const totalColumns = used.reduce(
    (widest, row) => Math.max(widest, Array.isArray(row) ? row.length : 0),
    0
  );

  const width = Math.min(totalColumns, MAX_PREVIEW_COLUMNS);
  const capped = used.slice(0, MAX_PREVIEW_ROWS).map((row) => {
    const cells: string[] = [];
    for (let column = 0; column < width; column += 1) {
      cells.push(asText(Array.isArray(row) ? row[column] : undefined));
    }
    return cells;
  });

  return {
    name,
    rows: capped,
    totalRows,
    totalColumns,
    truncated: totalRows > MAX_PREVIEW_ROWS || totalColumns > MAX_PREVIEW_COLUMNS,
  };
}

/**
 * A parsed workbook → the sheets worth showing.
 *
 * Empty sheets are dropped rather than rendered as a blank tab; a workbook
 * whose sheets are ALL empty still returns one, so the reader says "this sheet
 * is empty" instead of falling back to a download link for a file it read
 * perfectly well.
 */
export function toWorkbookPreview(
  sheetNames: string[],
  gridFor: (name: string) => unknown[][]
): WorkbookPreview {
  const previews = (sheetNames || []).map((name) => toSheetPreview(name, gridFor(name)));
  const withContent = previews.filter((sheet) => sheet.rows.length > 0);
  return { sheets: withContent.length ? withContent : previews.slice(0, 1) };
}

/** Spreadsheet formats SheetJS can read. `.xls` included - it is legacy, not lost. */
export const SPREADSHEET_EXTENSIONS = ['xlsx', 'xlsm', 'xlsb', 'xls', 'ods'];

export const isSpreadsheetExtension = (ext: string): boolean =>
  SPREADSHEET_EXTENSIONS.includes(String(ext || '').toLowerCase());

/** `A`, `B`, … `AA` - the column letters people expect above a sheet. */
export function columnLabel(index: number): string {
  let label = '';
  let n = index;
  while (n >= 0) {
    label = String.fromCharCode((n % 26) + 65) + label;
    n = Math.floor(n / 26) - 1;
  }
  return label;
}
